import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { AnimatePresence, MotionConfig, motion } from "framer-motion";
import { Toaster } from "sonner";
import { ThemeProvider, useTheme } from "@/lib/theme";
import { SiteProvider, useSite } from "@/lib/site";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { PageLoader } from "@/components/common";
import Home from "@/pages/Home";
import Work from "@/pages/Work";
import CaseStudy from "@/pages/CaseStudy";
import Services from "@/pages/Services";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import NotFound from "@/pages/NotFound";
import { AuthCallback } from "@/admin/auth";
import { useAnalytics } from "@/lib/analytics";

const AdminApp = lazy(() => import("@/admin/AdminApp"));

const PublicAnalytics = () => {
  const { settings } = useSite();
  useAnalytics(settings?.analytics_id || null);
  return null;
};

const AnimatedRoutes = () => {
  const location = useLocation();
  const { loading, settings } = useSite();
  if (loading && !settings) return <PageLoader />;
  return (
    <AnimatePresence mode="wait" onExitComplete={() => window.scrollTo(0, 0)}>
      <motion.main
        id="main"
        key={location.pathname}
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      >
        <Routes location={location}>
          <Route path="/" element={<Home />} />
          <Route path="/work" element={<Work />} />
          <Route path="/work/:slug" element={<CaseStudy />} />
          <Route path="/services" element={<Services />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </motion.main>
    </AnimatePresence>
  );
};

const Shell = () => {
  const { theme } = useTheme();
  const { pathname, hash } = useLocation();
  if (hash?.includes("session_id=")) return <AuthCallback />;
  if (pathname.startsWith("/admin"))
    return (
      <>
        <Suspense fallback={<PageLoader />}>
          <AdminApp />
        </Suspense>
        <Toaster theme="dark" position="bottom-right" richColors />
      </>
    );
  return (
    <div className="App noise">
      <PublicAnalytics />
      <Header />
      <AnimatedRoutes />
      <Footer />
      <Toaster theme={theme} position="bottom-right" />
    </div>
  );
};

export default function App() {
  return (
    <ThemeProvider>
      <MotionConfig reducedMotion="user">
        <SiteProvider>
          <BrowserRouter>
            <Shell />
          </BrowserRouter>
        </SiteProvider>
      </MotionConfig>
    </ThemeProvider>
  );
}
